import {
  type ClassificationLevel,
  CSRF_HEADER,
  classificationSchemeSchema,
  classificationUsageResponseSchema,
  NATIVE_CLIENTS_DEFAULT,
  type NativeClientsPolicy,
  type SettingsCatalogue,
  type SettingsCatalogueEntry,
} from "@esi/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ParseKeys } from "i18next";
import { CornerLeftUp, Lock, PenLine } from "lucide-react";
import { type RefObject, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ApiError, apiError, ok } from "../api-errors.ts";
import { formatDisjunction, formatList } from "../i18n/format.ts";
import { i18n, t as translate } from "../i18n/i18n.ts";
import { rejectPending } from "../projects/access-requests.ts";
import { GuidelinesEditor } from "../projects/settings/BehaviourSection.tsx";
import type { Guidelines } from "../projects/settings/behaviour.ts";
import { describePlacement, PlacementPathsEditor } from "../projects/settings/PlacementEditors.tsx";
import { describeReviewPeriod } from "../projects/settings/review-period.ts";
import {
  DomainsEditor,
  describeDomains,
  describeMaxAge,
} from "../projects/settings/WebSearchEditors.tsx";
import {
  adminSettingsPath,
  type BackofficeSection,
  Link,
  navigate,
  SETTINGS_GROUPS,
  type SettingsGroup,
  useSearch,
} from "../router.tsx";
import { useSession } from "../session.tsx";
import { describeEgress } from "../settings-shell/egress.ts";
import { EditorDisclosure } from "../settings-shell/row-actions.tsx";
import { SettingsGroupTabs } from "../settings-shell/SettingsGroupTabs.tsx";
import { type SearchEntry, SettingsSearch } from "../settings-shell/SettingsSearch.tsx";
import {
  SettingsGroupPage,
  SettingsSectionBlock,
  SettingsShell,
} from "../settings-shell/SettingsShell.tsx";
import { SummaryLines } from "../settings-shell/SummaryLines.tsx";
import { settingHash, useGroupFocus } from "../settings-shell/setting-anchors.ts";
import { SETTING_COPY as COPY } from "../settings-shell/setting-copy.ts";
import {
  type EditorPanelProps,
  INVALID,
  TypedSettingRow,
} from "../settings-shell/TypedSettingRow.tsx";
import { useFirstGroupRedirect } from "../settings-shell/use-first-group-redirect.ts";
import type { SettingAction } from "../settings-shell/use-setting-save.ts";
import { myAccessRequestsKey } from "./AccessRequests.tsx";
import { useAdminApi } from "./admin-api.tsx";
import { LevelList } from "./classification/LevelList.tsx";
import { LevelsDisclosure } from "./classification/ReadOnlyLevels.tsx";
import { ReviewBlock } from "./classification/ReviewBlock.tsx";
import { schemeReview, type Usage } from "./classification/review.ts";
import { editProblems } from "./classification/validation.ts";
import { adminKeys, adminRefusal, treeRows, useAdminEntities } from "./entity-tree.ts";
import { McpGroupTools } from "./McpGroupTools.tsx";
import { NativeClientsEditor } from "./NativeClientsEditor.tsx";
import { isSetHere, provenanceOf } from "./ProvenanceMark.tsx";
import { GROUP_RELATED, groupOf, sectionOf, sectionsOf } from "./settings-groups.ts";

/** Every key the backoffice has copy for (each must sit on one settings page). */
export const knownSettingKeys = (): string[] => Object.keys(COPY);

const AREAS = [
  "auth",
  "directory",
  "answers",
  "conversations",
  "models",
  "tools",
  "projects",
  "knowledge",
  "wiki",
  "memory",
  "mcp",
] as const;
const areaLabel = (area: string) =>
  (AREAS as readonly string[]).includes(area)
    ? translate(`adminSettings.areas.${area as (typeof AREAS)[number]}`)
    : area;

/**
 * Settings administration (spec `tenant-administration` "Settings administration with
 * inheritance"): for the organisation or one entity, every key the admin may set, on one page
 * per group (`/settings/<group>`, the level in `?level=`), each opening with a summary of what
 * is set at this level and locked above. Each row shows its value, where it comes from and its
 * lock; values fixed above (or by the instance) are read-only with the reason, and a level's own
 * value can be reset to the inherited one. Without a group, the first one replaces the URL.
 */
export function SettingsAdmin({ group }: { group: SettingsGroup | null }) {
  const { base } = useAdminApi();
  const search = useSearch();
  const level = search.get("level");
  // A link to one setting opens its page; anything else, the first page.
  useFirstGroupRedirect({
    group,
    first: SETTINGS_GROUPS[0],
    groupOfKey: groupOf,
    pathOf: (g) => adminSettingsPath(g, base, level),
  });
  return group === null ? null : <SettingsPages group={group} level={level} />;
}

/** A setting's page with its anchor. */
const settingPath = (key: string, base: string, level: string | null) =>
  `${adminSettingsPath(groupOf(key), base, level)}${settingHash(key)}`;

function SettingsPages(props: { group: SettingsGroup; level: string | null }) {
  const { group } = props;
  const { t, i18n } = useTranslation();
  const { base } = useAdminApi();
  const state = useLevelCatalogue(props.level ?? undefined);
  const { level, catalogue } = state;
  const heading = useRef<HTMLHeadingElement>(null);
  const known = catalogue.data?.settings.filter((s) => COPY[s.key]) ?? [];
  const data = catalogue.data;
  const pick = useGroupFocus({
    group,
    settings: data?.settings,
    groupOf,
    pathOf: (key) => settingPath(key, base, level),
    heading,
  });
  const scheme =
    (catalogue.data?.settings.find((s) => s.key === "knowledge.classificationScheme")?.value as
      | ClassificationLevel[]
      | undefined) ?? [];
  // Worded once per catalogue and language, not on every keystroke.
  const { language } = i18n;
  // biome-ignore lint/correctness/useExhaustiveDependencies: the words follow the language.
  const entries = useMemo<SearchEntry[]>(() => {
    const settings = data?.settings ?? [];
    const levels =
      (settings.find((s) => s.key === "knowledge.classificationScheme")?.value as
        | ClassificationLevel[]
        | undefined) ?? [];
    return settings
      .filter((s) => COPY[s.key])
      .map((s) => ({
        key: s.key,
        ...settingText(s.key),
        group: translate(`adminSettings.groups.${groupOf(s.key)}.label`),
        section: translate(`adminSettings.sections.${sectionOf(s.key)}`),
        value: describe(s, s.value, levels),
      }));
  }, [data, language]);

  return (
    <div className="flex flex-col gap-6">
      <SettingsShell
        title={t("adminSettings.title")}
        lead={t("adminSettings.lead")}
        notices={state.forbidden ? <Notice>{t("adminSettings.adminsOnly")}</Notice> : null}
        controls={
          state.forbidden ? null : (
            <>
              <LevelPicker
                state={state}
                onPick={(next) => navigate(adminSettingsPath(group, base, next), { replace: true })}
              />
              <SettingsSearch entries={entries} onPick={pick} />
            </>
          )
        }
        tabs={state.forbidden ? null : <SettingsNav group={group} level={level} settings={known} />}
      >
        {state.forbidden ? null : catalogue.isError || state.entities.isError ? (
          <Notice tone="danger">{(catalogue.error ?? state.entities.error)?.message}</Notice>
        ) : catalogue.data ? (
          <GroupPage
            // Keyed by level: an open editor never carries a draft over to another level.
            key={`${group}:${catalogue.data.level.entityId ?? "tenant"}`}
            group={group}
            catalogue={catalogue.data}
            settings={known}
            scheme={scheme}
            headingRef={heading}
            onReveal={pick}
          />
        ) : (
          <Skeleton className="h-64 w-full" />
        )}
      </SettingsShell>
    </div>
  );
}

/** The settings pages as link tabs, each with how many settings are set at this level. */
function SettingsNav(props: {
  group: SettingsGroup;
  level: string | null;
  settings: readonly SettingsCatalogueEntry[];
}) {
  const { t } = useTranslation();
  const { base } = useAdminApi();
  return (
    <SettingsGroupTabs
      label={t("adminSettings.navLabel")}
      groups={SETTINGS_GROUPS}
      current={props.group}
      href={(g) => adminSettingsPath(g, base, props.level)}
      groupLabel={(g) => t(`adminSettings.groups.${g}.label`)}
      count={(g) => props.settings.filter((s) => isSetHere(s) && groupOf(s.key) === g).length}
      countLabel={(_, count) => t("adminSettings.setHereCount", { count })}
    />
  );
}

const RELATED_TITLE: Partial<Record<BackofficeSection, ParseKeys>> = {
  directory: "backoffice.directory",
  signin: "backoffice.signin",
  models: "backoffice.models",
  residency: "backoffice.residency",
  memory: "backoffice.memory",
  branding: "branding.title",
  plan: "plan.title",
};

/** One settings page: its heading and purpose, the summary, then its sections of rows. */
function GroupPage(props: {
  group: SettingsGroup;
  catalogue: SettingsCatalogue;
  settings: readonly SettingsCatalogueEntry[];
  scheme: readonly ClassificationLevel[];
  headingRef: RefObject<HTMLHeadingElement | null>;
  onReveal: (key: string) => void;
}) {
  const { group, catalogue, scheme } = props;
  const { t } = useTranslation();
  const { base } = useAdminApi();
  const settings = props.settings.filter((s) => groupOf(s.key) === group);
  const sections = sectionsOf(group, settings);
  const related = GROUP_RELATED[group];
  return (
    <SettingsGroupPage
      id={group}
      heading={t(`adminSettings.groups.${group}.label`)}
      headingRef={props.headingRef}
      purpose={t(`adminSettings.groups.${group}.purpose`)}
      summary={
        <GroupSummary
          settings={settings}
          scheme={scheme}
          organisation={catalogue.level.entityId === null}
          onReveal={props.onReveal}
        />
      }
      related={
        related.length > 0 ? (
          <p className="text-xs text-fg-muted">
            {t("adminSettings.related")}{" "}
            {related.map((section, i) => {
              const title = RELATED_TITLE[section];
              return (
                <span key={section}>
                  {i > 0 ? ", " : null}
                  {/* Underlined at rest: colour alone doesn't set a link apart in text (#338). */}
                  <Link
                    href={`${base}/${section}`}
                    className="text-accent-text underline underline-offset-4"
                  >
                    {title ? t(title) : section}
                  </Link>
                </span>
              );
            })}
          </p>
        ) : null
      }
    >
      {sections.map((section) => (
        <SettingsSectionBlock
          key={section.id}
          id={section.id}
          heading={t(`adminSettings.sections.${section.id}`)}
        >
          {section.settings.map((setting) => (
            <SettingRow
              // Kept across refetches: the server value shows once nothing is pending (D2).
              key={setting.key}
              level={catalogue.level.entityId}
              setting={setting}
              scheme={scheme}
              settings={catalogue.settings}
            />
          ))}
        </SettingsSectionBlock>
      ))}
    </SettingsGroupPage>
  );
}

/**
 * What a page holds at this level, in a line or two: the settings set here and those locked
 * above, with their values, each a link to its row; or that nothing is set here.
 */
function GroupSummary(props: {
  settings: readonly SettingsCatalogueEntry[];
  scheme: readonly ClassificationLevel[];
  organisation: boolean;
  onReveal: (key: string) => void;
}) {
  const { t } = useTranslation();
  // A value locked above wins over this level's own one: it is not set here.
  const items = (settings: readonly SettingsCatalogueEntry[]) =>
    settings.map((s) => ({
      key: s.key,
      label: settingText(s.key).label,
      value: describe(s, s.value, props.scheme),
    }));
  return (
    <SummaryLines
      lines={[
        {
          id: "setHere",
          icon: PenLine,
          lead: t("adminSettings.summary.setHere"),
          items: items(props.settings.filter(isSetHere)),
        },
        {
          id: "lockedAbove",
          icon: Lock,
          lead: t("adminSettings.summary.lockedAbove"),
          items: items(props.settings.filter((s) => s.lockedBy !== null)),
        },
      ]}
      empty={{
        icon: CornerLeftUp,
        text: props.organisation
          ? t("adminSettings.summary.allDefault", { count: props.settings.length })
          : t("adminSettings.summary.allInherited", { count: props.settings.length }),
      }}
      onReveal={props.onReveal}
    />
  );
}

/** The entities in scope, the level shown (picked, else the default) and its catalogue. */
function useLevelCatalogue(picked: string | null | undefined) {
  const { admin } = useAdminApi();
  const entities = useAdminEntities();
  const rows = entities.data ? treeRows(entities.data) : [];
  // Tenant admin carries down every entity, so the session's roles tell; an entity admin of the
  // root sees the root entity but not the tenant level.
  const tenantAdmin = useSession().roles.includes("tenant_admin");
  const fallback = tenantAdmin ? null : (rows[0]?.entity.id ?? null);
  // A level outside the scope (an old link) shows the default one; the server decides anyway.
  const level =
    picked === undefined ||
    (picked !== null && entities.data && !rows.some((r) => r.entity.id === picked)) ||
    (picked === null && !tenantAdmin)
      ? fallback
      : picked;
  const ready = entities.isSuccess;
  const catalogue = useQuery({
    queryKey: adminKeys.catalogue(level),
    enabled: ready,
    queryFn: async (): Promise<SettingsCatalogue> =>
      ok(
        await admin.settings.catalogue.$get({
          query: level ? { entity: level } : {},
        }),
      ),
    retry: false,
  });
  const forbidden =
    (entities.error instanceof ApiError && entities.error.status === 403) ||
    (catalogue.error instanceof ApiError && catalogue.error.status === 403);
  return { entities, rows, tenantAdmin, level, ready, catalogue, forbidden };
}

/** The level select: the organisation (tenant admins) and every entity in scope, indented. */
function LevelPicker(props: {
  state: ReturnType<typeof useLevelCatalogue>;
  onPick: (level: string | null) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const { state } = props;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`${id}-level`}>{t("adminSettings.level")}</Label>
      <NativeSelect
        id={`${id}-level`}
        value={state.level ?? ""}
        disabled={!state.ready}
        onChange={(e) => props.onPick(e.currentTarget.value || null)}
      >
        {state.tenantAdmin ? <option value="">{t("adminSettings.organisation")}</option> : null}
        {state.rows.map((r) => (
          <option key={r.entity.id} value={r.entity.id}>
            {`${"\u2003".repeat(r.depth)}${r.entity.name}`}
          </option>
        ))}
      </NativeSelect>
    </div>
  );
}

/**
 * The level picker and, for the picked level, its settings rows; with `keys`, only those (the
 * memory screen). Shared by the topic screens built on the settings.
 */
export function SettingsLevels(props: { keys: readonly string[] }) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<string | null | undefined>(undefined);
  const state = useLevelCatalogue(picked);
  const { catalogue } = state;

  return (
    <div className="flex flex-col gap-8">
      {state.forbidden ? (
        <Notice>{t("adminSettings.adminsOnly")}</Notice>
      ) : (
        <>
          <div className="sm:max-w-sm">
            <LevelPicker state={state} onPick={setPicked} />
          </div>
          {catalogue.isError || state.entities.isError ? (
            <Notice tone="danger">{(catalogue.error ?? state.entities.error)?.message}</Notice>
          ) : catalogue.data ? (
            // Keyed by level: an open editor never carries a draft over to another level.
            <Catalogue
              key={catalogue.data.level.entityId ?? "tenant"}
              catalogue={catalogue.data}
              keys={props.keys}
            />
          ) : (
            <Skeleton className="h-64 w-full" />
          )}
        </>
      )}
    </div>
  );
}

function Catalogue({ catalogue, keys }: { catalogue: SettingsCatalogue; keys: readonly string[] }) {
  const known = catalogue.settings.filter((s) => COPY[s.key] && keys.includes(s.key));
  const areas = [...new Set(known.map((s) => s.area))].sort(
    (a, b) => (AREAS as readonly string[]).indexOf(a) - (AREAS as readonly string[]).indexOf(b),
  );
  const scheme =
    (catalogue.settings.find((s) => s.key === "knowledge.classificationScheme")?.value as
      | ClassificationLevel[]
      | undefined) ?? [];
  return (
    <div className="flex flex-col gap-6">
      {areas.map((area) => (
        // A topic screen names its section itself.
        <section key={area} aria-label={areaLabel(area)} className="flex flex-col gap-2">
          <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
            {known
              .filter((s) => s.area === area)
              .map((setting) => (
                <SettingRow
                  // Kept across refetches: the server value shows once nothing is pending (D2).
                  key={setting.key}
                  level={catalogue.level.entityId}
                  setting={setting}
                  scheme={scheme}
                  settings={catalogue.settings}
                />
              ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** The value in words. */
export function describe(
  setting: SettingsCatalogueEntry,
  value: unknown,
  scheme: readonly ClassificationLevel[],
): string {
  switch (setting.key) {
    case "models.residency":
      return translate(
        value === "global"
          ? "settings.behaviour.residency.global"
          : "settings.behaviour.residency.eu",
      );
    case "models.allowed":
      return (Array.isArray(value) ? value : []).map((role) => modelRole(String(role))).join(", ");
    case "models.defaultEffort":
      return modelRole(String(value));
    case "knowledge.defaultClassification":
      return scheme.find((l) => l.id === value)?.label ?? String(value);
    case "knowledge.classificationScheme":
      return (Array.isArray(value) ? (value as ClassificationLevel[]) : [])
        .map((l) => l.label)
        .join(" < ");
    case "auth.mfa":
      return translate(
        value === "required" ? "adminSettings.mfa.required" : "adminSettings.mfa.optional",
      );
    case "mcp.classificationCeiling":
      return describeCeiling(value, scheme);
    case "auth.nativeClients": {
      const policy = value as NativeClientsPolicy;
      if (policy.mode !== "allowlist") {
        return translate("adminSettings.nativeClients.any");
      }
      // An empty list reads as such, never "Only 0 listed applications" (#365).
      return policy.clients.length === 0
        ? translate("adminSettings.nativeClients.summaryNone")
        : translate("adminSettings.nativeClients.summary", { count: policy.clients.length });
    }
    case "memory.retentionDays":
      return translate("settings.behaviour.web.days", { count: Number(value) });
    case "directory.idpGroupsMaxAge":
      return translate("adminSettings.hours", { count: Number(value) });
    case "knowledge.maxUploadMb":
      return translate("adminSettings.megabytes", { count: Number(value) });
    case "models.turnMaxUsd":
      return translate("adminSettings.usd", { count: Number(value) });
    case "models.sharePressureThresholdPct":
      return translate("adminSettings.percent", { count: Number(value) });
    case "knowledge.ocrMaxPages":
      return translate("settings.behaviour.pages", { count: Number(value) });
    case "knowledge.ocrMonthlyPages":
      return value === null
        ? translate("adminSettings.operatorBudget")
        : translate("settings.behaviour.pages", { count: Number(value) });
    case "tools.webSearch.provider":
    case "tools.webFetch.provider":
    case "tools.codeExecution.provider":
      return providerName(String(value));
    case "tools.webSearch.domains":
    case "tools.webFetch.domains":
      return describeDomains(value);
    case "tools.webFetch.approval":
      return translate(
        value === "always"
          ? "settings.behaviour.webFetchApproval.always"
          : "settings.behaviour.webFetchApproval.provenance",
      );
    case "tools.webSearch.maxAgeDays":
      return describeMaxAge(value);
    case "tools.egress":
    case "tools.codeExecution.egress":
      return describeEgress(value) ?? JSON.stringify(value);
    case "wiki.trust":
      return ["requester", "peer", "manager"].includes(String(value))
        ? translate(`settings.behaviour.wikiTrust.${value as "requester" | "peer" | "manager"}`)
        : String(value);
    case "wiki.placement":
      return describePlacement(value);
    case "wiki.reviewAfterDays":
      return describeReviewPeriod(value);
    case "wiki.guidelines": {
      const g = (value ?? { pageTypes: [] }) as Guidelines;
      const types = g.pageTypes.map((p) => translate(`wiki.pageTypes.${p}`)).join(", ");
      return translate("settings.behaviour.guidelines.summary", {
        language: g.language || translate("settings.behaviour.guidelines.sourcesLanguage"),
        types,
      });
    }
  }
  if (typeof value === "boolean") {
    return translate(value ? "settings.behaviour.on" : "settings.behaviour.off");
  }
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : JSON.stringify(value);
}

const FLOOR_RANK: Readonly<Record<string, number>> = { project: 0, entity: 1, named: 2 };

/** A readers-floor ceiling in the tenant's own level names: "Public and Internal may be returned". */
export function describeCeiling(value: unknown, scheme: readonly ClassificationLevel[]): string {
  const ceiling = FLOOR_RANK[String(value)] ?? -1;
  const levels = scheme
    .filter((l) => (FLOOR_RANK[l.readersFloor] ?? 2) <= ceiling)
    .map((l) => l.label);
  if (levels.length === 0) {
    return translate("adminSettings.mcpCeiling.none");
  }
  const list = new Intl.ListFormat(i18n.language, { type: "conjunction" }).format(levels);
  return translate("adminSettings.mcpCeiling.upTo", { levels: list });
}

const PROVIDERS = ["agentcore", "lambda"] as const;
const providerName = (provider: string) =>
  (PROVIDERS as readonly string[]).includes(provider)
    ? translate(`adminSettings.providers.${provider as (typeof PROVIDERS)[number]}`)
    : provider;

const MODEL_ROLES = ["chat", "reasoning", "fast"] as const;
const modelRole = (role: string) =>
  (MODEL_ROLES as readonly string[]).includes(role)
    ? translate(`adminSettings.modelRoles.${role as (typeof MODEL_ROLES)[number]}`)
    : role;

/** Why the deployment fixes a value, when its mark alone does not say it (residency). */
function instanceReason(setting: SettingsCatalogueEntry): string | null {
  return setting.lockedBy?.level === "instance" && setting.key === "models.residency"
    ? translate("adminSettings.instanceResidency")
    : null;
}

/** A setting's label and help, as the backoffice words them (keys the copy does not know: the key). */
export function settingText(key: string): { label: string; help: string } {
  const copy = Object.hasOwn(COPY, key) ? COPY[key] : undefined;
  if (!copy) {
    return { label: key, help: "" };
  }
  return {
    label: translate(
      `settings.behaviour.keys.${copy}.label` as "settings.behaviour.keys.ocr.label",
    ),
    help: translate(`settings.behaviour.keys.${copy}.help` as "settings.behaviour.keys.ocr.help"),
  };
}

/** The key whose editor is the classification levels list (openspec change classification-editor). */
const SCHEME_KEY = "knowledge.classificationScheme";

/** The saved and edited schemes, when both parse (the review and its checks need levels). */
function schemes(saved: unknown, edited: unknown) {
  const before = classificationSchemeSchema.safeParse(saved);
  return before.success && Array.isArray(edited)
    ? { before: before.data, after: edited as ClassificationLevel[] }
    : null;
}

function SettingRow(props: {
  level: string | null;
  setting: SettingsCatalogueEntry;
  scheme: readonly ClassificationLevel[];
  /** The page's catalogue: the scheme editor words its levels from the tenant's settings. */
  settings: readonly SettingsCatalogueEntry[];
}) {
  const { admin, readOnly } = useAdminApi();
  const { level, setting, scheme } = props;
  const { t } = useTranslation();
  const { csrfToken, roles } = useSession();
  const queryClient = useQueryClient();
  const isScheme = setting.key === SCHEME_KEY;
  const { label, help } = settingText(setting.key);
  const editable = setting.lockedBy === null && !readOnly;
  // Where the scheme cannot be changed, its levels open read-only (classification-editor D9).
  const [meaning, setMeaning] = useState(false);
  const tenantAdmin = roles.includes("tenant_admin");
  // What uses each level, read by whoever may count it (a tenant admin or a support grant;
  // entity admins never ask); an older API (404) gives none.
  const usage = useSchemeUsage(isScheme && !editable && meaning && (tenantAdmin || readOnly));
  const reason = instanceReason(setting);
  const headers = { headers: { [CSRF_HEADER]: csrfToken } };
  // Every level's catalogue may change (inheritance), and the residency report reads the same rows.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "settings"] }),
      queryClient.invalidateQueries({ queryKey: ["admin", "residency"] }),
      // Residency and efforts change what the Models screen shows.
      queryClient.invalidateQueries({ queryKey: ["admin", "models"] }),
      queryClient.invalidateQueries({ queryKey: myAccessRequestsKey }),
      queryClient.invalidateQueries({ queryKey: ["admin", "classification-usage"] }),
    ]);
  const run = async (action: SettingAction) => {
    if (action.type === "put") {
      const json = { value: action.value, locked: action.locked };
      return level === null
        ? ok(
            rejectPending(
              await admin.settings[":key"].$put({ param: { key: setting.key }, json }, headers),
            ),
          )
        : ok(
            rejectPending(
              await admin.entities[":entityId"].settings[":key"].$put(
                { param: { entityId: level, key: setting.key }, json },
                headers,
              ),
            ),
          );
    }
    const res =
      level === null
        ? await admin.settings[":key"].$delete({ param: { key: setting.key } }, headers)
        : await admin.entities[":entityId"].settings[":key"].$delete(
            { param: { entityId: level, key: setting.key } },
            headers,
          );
    if (!res.ok) {
      throw await apiError(res);
    }
    rejectPending(res);
  };
  // Efforts outside the allowed ones cannot be the default.
  const allowed = props.settings.find((s) => s.key === "models.allowed")?.value;
  const disabledOptions =
    setting.key === "models.defaultEffort" && Array.isArray(allowed)
      ? (setting.input.type === "select" ? setting.input.options : []).filter(
          (r) => !allowed.includes(r),
        )
      : undefined;
  return (
    <TypedSettingRow
      settingKey={setting.key}
      input={setting.input}
      label={label}
      help={help}
      helpExtra={<McpGroupTools settingKey={setting.key} />}
      value={setting.value}
      locked={setting.lockedHere}
      editable={editable}
      lockable={editable}
      lockCopy="lock"
      resettable={editable && setting.ownValue}
      resetText={t("settings.behaviour.reset")}
      // What a Reset brings back: when it loosens, the row asks first, for every loosening key
      // (#365); a tightening Reset saves at once. An N-1 API sends no inherited value: only
      // auth.nativeClients (tenant-wide, never an instance policy) knows its own, the default.
      resetTo={
        setting.inherited ??
        (setting.key === "auth.nativeClients" ? { value: NATIVE_CLIENTS_DEFAULT } : undefined)
      }
      setHere={isSetHere(setting)}
      lockedAbove={setting.lockedBy !== null}
      marks={provenanceOf(setting)}
      unmarked={t("settings.behaviour.source.instance")}
      extra={
        <>
          {reason ? <span>{reason}</span> : null}
          {setting.unavailable ? <span>{t("adminSettings.unavailable")}</span> : null}
        </>
      }
      describe={(value) => describe(setting, value, scheme)}
      optionLabel={
        setting.key === "models.allowed" ? modelRole : (o) => describe(setting, o, scheme)
      }
      disabledOptions={disabledOptions}
      run={run}
      settle={refresh}
      refusal={adminRefusal}
      editor={({ id, value, onChange }) => (
        <Editor id={id} setting={setting} scheme={scheme} value={value} onChange={onChange} />
      )}
      editorPanel={
        isScheme
          ? (panel) => <SchemeEditorPanel {...panel} setting={setting} settings={props.settings} />
          : undefined
      }
      below={
        isScheme && !editable ? (
          <SchemeReadOnly
            setting={setting}
            settings={props.settings}
            usage={usage.isSuccess ? (usage.data ?? undefined) : undefined}
            open={meaning}
            onToggle={() => setMeaning(!meaning)}
          />
        ) : null
      }
    />
  );
}

/** What uses each classification level, when `enabled`; an older API (404) gives none. */
function useSchemeUsage(enabled: boolean) {
  const { admin } = useAdminApi();
  return useQuery({
    queryKey: ["admin", "classification-usage"],
    enabled,
    queryFn: async (): Promise<Usage> => {
      const res = await admin.classification.usage.$get();
      if (res.status === 404) {
        return null;
      }
      return classificationUsageResponseSchema.parse(await ok(res)).levels;
    },
  });
}

/**
 * The classification levels editor ("Edit levels"): the levels list, then "Before you save",
 * which reviews a changed scheme and holds #262's confirmation for loosening a named-readers
 * level's egress floor (openspec change classification-editor), unchanged inside.
 */
function SchemeEditorPanel(
  props: EditorPanelProps & {
    setting: SettingsCatalogueEntry;
    settings: readonly SettingsCatalogueEntry[];
  },
) {
  const { t } = useTranslation();
  const { id, setting } = props;
  const [value, setValue] = useState<unknown>(setting.value);
  const [reviewing, setReviewing] = useState(false);
  const usage = useSchemeUsage(true);
  const valueChanged = JSON.stringify(value) !== JSON.stringify(setting.value);
  const edited = schemes(setting.value, value);
  const problems = edited ? editProblems(edited.before, edited.after) : null;
  // Known counts, null from an older API (404), undefined while loading or after an error.
  const usageNow = usage.isSuccess ? usage.data : undefined;
  const reviewed =
    edited && valueChanged
      ? schemeReview(edited.before, edited.after, usageNow, {
          and: formatList,
          or: formatDisjunction,
        })
      : null;
  // A value that changed without changing the scheme's meaning (a floor equal to its readers
  // dropped) has nothing to review.
  const review = reviewed && reviewed.items.length > 0 ? reviewed : null;
  const blocked = value === INVALID || (problems !== null && problems.count > 0);
  const saved = classificationSchemeSchema.safeParse(setting.value);
  return (
    <EditorDisclosure
      labelledBy={props.labelId}
      onCancel={props.close}
      onSubmit={() => {
        if (valueChanged && !props.saving && !blocked) {
          if (review && !reviewing) {
            setReviewing(true);
          } else {
            props.save(value);
          }
        }
      }}
      notices={
        <>
          {props.refusal ? <Notice tone="danger">{props.refusal}</Notice> : null}
          {reviewing && review ? <ReviewBlock review={review} /> : null}
        </>
      }
      actions={
        <div
          className={cn(
            "flex flex-wrap items-center gap-2",
            // Phone (classification-editor D10): the review above, full-width actions below.
            "max-sm:flex-col max-sm:items-stretch max-sm:[&>button]:h-11",
          )}
        >
          <Button
            type="submit"
            size="sm"
            variant={reviewing && review?.loosensNamed ? "destructive" : "default"}
            disabled={!valueChanged || props.saving || blocked}
            aria-describedby={problems?.count ? `${id}-problems` : undefined}
          >
            {props.saving
              ? t("settings.saving")
              : reviewing && review
                ? review.loosensNamed
                  ? t("adminSettings.egress.confirm")
                  : t("settings.save")
                : review
                  ? t("adminSettings.levels.reviewAndSave")
                  : t("settings.save")}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={props.close}>
            {t("settings.cancel")}
          </Button>
          {problems?.count ? (
            <span id={`${id}-problems`} className="text-xs text-destructive">
              {t("adminSettings.levels.fixToSave", { count: problems.count })}
            </span>
          ) : null}
        </div>
      }
    >
      <LevelList
        id={id}
        saved={saved.success ? saved.data : []}
        value={value}
        onChange={(next) => {
          props.clear();
          setReviewing(false);
          setValue(next);
        }}
        settings={props.settings}
        usage={usageNow}
      />
    </EditorDisclosure>
  );
}

/** The scheme's levels, read-only, behind "What these levels mean" (D9). */
function SchemeReadOnly(props: {
  setting: SettingsCatalogueEntry;
  settings: readonly SettingsCatalogueEntry[];
  usage: Usage | undefined;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const scheme = classificationSchemeSchema.safeParse(props.setting.value);
  return scheme.success ? (
    <LevelsDisclosure
      scheme={scheme.data}
      settings={props.settings}
      usage={props.usage}
      label={t("adminSettings.levels.listLabel")}
      open={props.open}
      onToggle={props.onToggle}
    />
  ) : null;
}

/**
 * The body of an explicit editor (lists and structured values, opened by a named button):
 * guidelines, placement, native clients and domain lists. Typed values (`tools.egress`
 * included) have their control on the row (`TypedSettingRow`).
 */
export function Editor(props: {
  id: string;
  setting: SettingsCatalogueEntry;
  scheme: readonly ClassificationLevel[];
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const { id, setting, value, onChange } = props;
  if (setting.key === "wiki.guidelines") {
    return <GuidelinesEditor id={id} value={value as Guidelines} onChange={onChange} />;
  }
  if (setting.key === "wiki.placement") {
    return <PlacementPathsEditor id={id} value={value} onChange={onChange} />;
  }
  if (setting.key === "auth.nativeClients") {
    return <NativeClientsEditor id={id} value={value as NativeClientsPolicy} onChange={onChange} />;
  }
  if (setting.key === "tools.webFetch.domains" || setting.input.type === "domains") {
    // Edited like web search's lists whatever input an older API describes.
    const max = setting.input.type === "domains" ? setting.input.max : 100;
    return <DomainsEditor id={id} value={value} max={max} onChange={onChange} />;
  }
  // Every other shown key has a control of its own (the control table's test checks it).
  return null;
}
